import { parseISOToLocalDate } from '../utils/dateUtils';

export const WOUND_TYPES: { value: string; label: string }[] = [
  { value: 'pressure_injury', label: 'Pressure Injury' },
  { value: 'arterial_ulcer', label: 'Arterial Ulcer' },
  { value: 'venous_ulcer', label: 'Venous Ulcer' },
  { value: 'bite', label: 'Bite' },
  { value: 'burn', label: 'Burn' },
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'infection', label: 'Infection' },
  { value: 'skin_lesion', label: 'Skin Lesion' },
  { value: 'skin_tear', label: 'Skin Tear' },
  { value: 'surgical_wound', label: 'Surgical Wound' },
  { value: 'traumatic', label: 'Traumatic' },
  { value: 'ostomy', label: 'Ostomy' },
  { value: 'blister', label: 'Blister' },
  { value: 'rash', label: 'Rash' },
  { value: 'cellulitis', label: 'Cellulitis' },
  { value: 'edema', label: 'Edema' },
  { value: 'surgical_incision', label: 'Surgical Incision' },
  { value: 'abrasion', label: 'Abrasion' },
  { value: 'masd', label: 'MASD' },
  { value: 'abscess', label: 'Abscess' },
  { value: 'dermatitis', label: 'Dermatitis' },
  { value: 'non_pressure_ulcer', label: 'Non-Pressure Ulcer' },
  { value: 'lymphedema', label: 'Lymphedema' },
  { value: 'laceration', label: 'Laceration' },
  { value: 'other', label: 'Other' },
];

export const DRAINAGE_TYPES: { value: string; label: string }[] = [
  { value: 'serous', label: 'Serous' },
  { value: 'serosanguineous', label: 'Serosanguineous' },
  { value: 'sanguineous', label: 'Sanguineous' },
  { value: 'purulent', label: 'Purulent' },
  { value: 'seropurulent', label: 'Seropurulent' },
  { value: 'other', label: 'Other' },
];

export const DRAINAGE_AMOUNTS: { value: string; label: string }[] = [
  { value: 'scant', label: 'Scant' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'copious', label: 'Copious' },
];

export const ODOR_TYPES: { value: string; label: string }[] = [
  { value: 'fecal', label: 'Fecal' },
  { value: 'foul', label: 'Foul' },
  { value: 'musty', label: 'Musty' },
  { value: 'pungent', label: 'Pungent' },
  { value: 'strong', label: 'Strong' },
  { value: 'sweet', label: 'Sweet' },
  { value: 'other', label: 'Other' },
];

export const THICKNESS_OPTIONS: { value: string; label: string }[] = [
  { value: 'full', label: 'Full' },
  { value: 'partial', label: 'Partial' },
  { value: 'epithelized', label: '100% Epithelized' },
  { value: 'unable_to_determine', label: 'Unable to Determine' },
  { value: 'other', label: 'Other' },
];

export const EDGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'attached', label: 'Attached' },
  { value: 'non_attached', label: 'Non-attached' },
  { value: 'epithelization', label: 'Epithelization' },
  { value: 'rolled_epibole', label: 'Rolled/Epibole' },
  { value: 'other', label: 'Other' },
];

export const SURROUNDING_TISSUE_OPTIONS: { value: string; label: string }[] = [
  { value: 'intact', label: 'Intact' },
  { value: 'calloused', label: 'Calloused' },
  { value: 'erythema', label: 'Erythema' },
  { value: 'excoriated', label: 'Excoriated' },
  { value: 'fragile_friable', label: 'Fragile/Friable' },
  { value: 'indurated', label: 'Indurated' },
  { value: 'macerated', label: 'Macerated' },
  { value: 'flesh_tone', label: 'Flesh Tone' },
  { value: 'dry', label: 'Dry' },
  { value: 'other', label: 'Other' },
];

export const PRESENCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_assessed', label: 'Not Assessed' },
];

export const WOUND_BED_TISSUE_TYPES: { key: string; label: string }[] = [
  { key: 'wound_bed_pink_red_pct', label: 'Pink/Red (Healthy)' },
  { key: 'wound_bed_granulation_pct', label: 'Granulation' },
  { key: 'wound_bed_slough_pct', label: 'Slough' },
  { key: 'wound_bed_eschar_pct', label: 'Eschar' },
  { key: 'wound_bed_necrotic_pct', label: 'Necrotic' },
];

export function getWoundTypeLabel(value: string): string {
  return WOUND_TYPES.find(t => t.value === value)?.label ?? value;
}

function formatWoundDate(dateStr: string): string {
  const d = parseISOToLocalDate(dateStr)!;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * The onset cell: present on admission is its own fact, and a known onset
 * date rides beside it. A wound acquired after admission with no recorded
 * onset reads as unknown, never as present on admission.
 */
export function formatWoundOnset(wound: {
  onset_date?: string | null;
  present_on_admission: boolean;
}): string {
  const dated = wound.onset_date ? formatWoundDate(wound.onset_date) : null;
  if (wound.present_on_admission) {
    return dated ? `Present on admission (${dated})` : 'Present on admission';
  }
  return dated ?? 'Unknown';
}
